import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from "@stardust-ds/react"
import moment from "moment"
import intl from "react-intl-universal"
import styles from "./index.module.scss"
import "./style.css"

import { fnCanAtLeast } from "../../../../../utils/permissions"
import { fnCreateQuickPublish } from "../../../../../services/document"
import { FILE_CLASSIFICATION_REVERSE } from "../../constants"

import useDataApi from '../../../../../hooks/useDataApi'
import PlusIcon from "../../../../../assets/icons/PlusIcons"
import DocumentIcon from "../../../../../assets/icons/DocumentIcon"
import TrashIcon from "../../../../../assets/icons/TrashIcon"
import RcmpHeader from "../../../../atoms/Header"
import Button from "../../../../atoms/Button"
import Input from "../../../../atoms/Input"
import RcmpSimpleSwitch from "../../../../atoms/SimpleSwitch"
import RcmpDatePicker from "../../../../atoms/DatePicker"
import RcmpLoading from "../../../../atoms/Loading"
import FileInput from "../../../../atoms/FileInput"
import RcmpSelectRequisiteNorms from "../../../../molecules/SelectRequisiteNorms"
import RcmpSelectUsersAndTeams from "../../../../molecules/SelectUsersAndTeams"
import RcmpSelectUsers from "../../../../molecules/SelectUsers"
import ContentBlank from "../../../../molecules/ContentBlank"

const ImportDocuments = () => {
  const [oCreateFormRes, fnCreateFromFormReq] = useDataApi(fnCreateQuickPublish)
  const [isLoading, setIsLoading] = useState(true)
  const [aFiles, fnSetAFiles] = useState([])
  const [isValidForm, setIsValidForm] = useState(false)
  const sizeFileMB = 10;
  const { folderId } = useParams()
  const canCreateFolder = localStorage.getItem('canCeeDocFolder');
  const navigate = useNavigate()

  const aBreadCrumbs = [
    {
      path: "./",
      name: intl.get("home"),
    },
    {
      path: "./folder",
      name: intl.get("document.title"),
    },
    {
      name: intl.get("document.sub_title"),
      bActive: true,
    },
  ]

  const publishFiles = async (event) => {
    try {
      event.preventDefault();

      if (!aFiles.length) {
        throw intl.get("document.no_files_to_publish")
      }

      // Verifica se há campos obrigatórios não preenchidos
      for (const file of aFiles) {
        if (file.is_required_due_date) {
          if (!file.expiration_in) {
            throw intl.get("document.document_error_due_date_required");
          }
          if (!file.notify_expiration) {
            throw intl.get("document.document_error_notify_expiration_required");
          }
        }
      }

      if (aFiles.some(file => !file.responsible)) {
        throw intl.get("document.no_responsible_selected")
      }

      const oResponse = await fnCreateFromFormReq({
        aFiles, fldr_nid: folderId
      })

      if (oResponse.status && oResponse.status !== 200) {
        throw oResponse.response.data.cMessage ?? intl.get("document.document_create_error")
      }

      fnSetAFiles([]);
      toast.success({
        title: intl.get("document.document_create_success"),
      })
    } catch (error) {
      toast.error({
        title: error,
      })
    }
  }

  const handleSelectOptions = (selected, property, index) => {
    const selectedArray = Array.isArray(selected) ? selected.map((item) => item.value) : selected

    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? { ...file, [property]: selectedArray } : file))
    )
  }

  const handleSelectUsersAndTeams = (selected, index) => {
    if (!selected || !Array.isArray(selected)) return

    const selectedArray = selected.reduce((acc, item) => {
      if (item.nUserId) {
        acc.users.push(item.nUserId)
      }
      if (item.nTeamId) {
        acc.teams.push(item.nTeamId)
      }

      return acc;
    }, { users: [], teams: [] })

    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? {
        ...file, viewers: selectedArray
      } : file))
    )
  }

  const handleRemoveFile = (index) => {
    fnSetAFiles((prevFiles) => prevFiles.filter((_, idx) => idx !== index));
  }

  const handleSetFileName = (element, index) => {
    const { value } = element.target;

    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? { ...file, opae_cfilename: value } : file))
    )
  }

  const handleSetDueDate = (date, index) => {
    const cFormatReturn = "YYYY-MM-DD"

    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? { ...file, expiration_in: moment(date).format(cFormatReturn) } : file))
    )
  }

  const handleActiveDueDate = (active, index) => {
    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? { ...file, is_required_due_date: active } : file))
    )
  }

  const handleNotifyExpiration = (value, index) => {
    fnSetAFiles((prevFiles) =>
      prevFiles.map((file, idx) => (idx === index ? { ...file, notify_expiration: value } : file))
    )
  }

  useEffect(() => {
    setIsLoading(false);
    if (!canCreateFolder && !fnCanAtLeast('admin')) { 
        navigate('/'); 
    }
  }, [navigate]);

  useEffect(() => {
    setIsValidForm(aFiles.some(file => !file.responsible))
  }, [aFiles]);

  const handleFileSubmit = (aFiles) => {
    try {
      if (!aFiles.length) return;

      aFiles.forEach((oFile) => {
        if (
          oFile.opae_cvalue &&
          !FILE_CLASSIFICATION_REVERSE[oFile.opae_cvalue.type]
        ) {
          throw new Error(intl.get("document.error_file_type", { type: oFile.opae_cvalue.type }))
        }

        if (!oFile) {
          throw new Error(intl.get("ActivityEvidence.file_max_size", { size: `${sizeFileMB}mb` }))
        }
      })

      fnSetAFiles((prevFiles) => [...prevFiles, ...aFiles]);
    } catch (error) {
      toast.error({
        title: error.message,
      })
    }
  };

  if (isLoading) {
    return (
      <div>
        <RcmpHeader
          cTitlePage={intl.get("document.title")}
          elIcon={<DocumentIcon />}
          className={styles.header}
          aBreadCrumbs={aBreadCrumbs}
        />
        <ContentBlank
          bSwitchable={false}
          className={styles.contentBlankCustom}
          sButtonLabel={intl.get("document.sub_title")}
          bVisibleIconSearch={false}
        >
          <div className={styles.loading}>
            <RcmpLoading bShow={true} size={60} />
          </div>
        </ContentBlank>
      </div>
    )
  }

  return (
    <div>
      <RcmpHeader
        elIcon={<DocumentIcon />}
        cTitlePage={intl.get("document.title")}
        aBreadCrumbs={aBreadCrumbs}
        className={styles.header}
      ></RcmpHeader>
      <form>
        <ContentBlank
          bShowFilter={false}
          bCanSearch={false}
          bArrowLeftGoBack={true}
          bVisibleIconSearch={false}
          bSwitchAtive={false}
          cWidthBtn={"15rem"}
          cIconLeftInsideBtn={<PlusIcon />}
          cPathGoBack={`${window.location.origin}/folder/${folderId}`}
          cLabelGoBack={intl.get("document.sub_title")}
          className={styles.contentB1lankCustom}
        >
          <div className={styles.containerDocument}>
            <div className={styles.containerLinks}>
              <FileInput fnFileSubmit={handleFileSubmit} nSizeFile={sizeFileMB} />
              {aFiles.length > 0 && (
                <>
                  <div className={styles.containerTable}>
                    <span className={styles.titleTableTop}>{intl.get("document.uploaded_files")}</span>
                    <table className={styles.table}>
                      <thead className={styles.head}>
                        <tr>
                          {[
                            intl.get("document.header_table_list_import_files.name"),
                            intl.get("document.header_table_list_import_files.format"),
                            intl.get("document.header_table_list_import_files.responsible"),
                            intl.get("document.header_table_list_import_files.user_permission"),
                            intl.get("document.header_table_list_import_files.required_norm"),
                            intl.get("document.expiration"),
                            intl.get("document.expiration_in"),
                            intl.get("document.notify_expiration"),
                            intl.get("document.actions"),
                          ].map((item, index) => (
                            <th
                              key={index}
                              id={item}
                              className={`${item.toLowerCase().includes("name") || item.includes("observation")
                                ? styles.widthLarge
                                : styles.widthNormal
                                } th`}
                            >
                              {item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {aFiles.map((file, index) => (
                          <tr key={index}>
                            <td>
                              <Input
                                placeholder={intl.get("type")}
                                value={file.opae_cfilename}
                                onChange={element => {
                                  handleSetFileName(element, index)
                                }}
                                disabled={false}
                              />
                            </td>
                            <td className={styles.fileClassification}>{FILE_CLASSIFICATION_REVERSE[file.opae_cvalue.type]}</td>
                            <td>
                              <RcmpSelectUsers
                                required
                                searchable
                                onSelect={element => {
                                  handleSelectOptions(element, 'responsible', index)
                                }}
                                value={file.responsible}
                                placeholder="Selecione para filtrar"
                              />
                            </td>
                            <td>
                              <RcmpSelectUsersAndTeams
                                multiSelect
                                searchable
                                onSelect={element => {
                                  handleSelectUsersAndTeams(element, index)
                                }}
                                placeholder="Selecione para filtrar"
                              />
                            </td>
                            <td>
                              <RcmpSelectRequisiteNorms
                                multiSelect
                                searchable
                                onSelect={element => {
                                  handleSelectOptions(element, 'norms', index)
                                }}
                                placeholder="Selecione para filtrar"
                              />
                            </td>
                            <td>
                              <RcmpSimpleSwitch
                                bShowActive={file.is_required_due_date}
                                onChange={element => {
                                  handleActiveDueDate(element, index)
                                }}
                              />
                            </td>
                            {file.is_required_due_date && (
                              <>
                                <td>
                                  <RcmpDatePicker
                                    className={styles.datePicker}
                                    bTime={false}
                                    onChange={(element) => {
                                      handleSetDueDate(element, index)
                                    }}
                                  />
                                </td>
                                <td>
                                  <Input
                                    placeholder={intl.get("modal_new_automation.days")}
                                    value={file.notify_expiration}
                                    type="number"
                                    onChange={element => {
                                      handleNotifyExpiration(element, index)
                                    }}
                                    disabled={false}
                                  />
                                </td>
                              </>
                            ) || (<><td></td><td></td></>)}
                            <td>
                              <TrashIcon
                                className={styles.trashIcon}
                                onClick={() => { handleRemoveFile(index) }}
                                width="30px"
                                height="30px"
                                fill="red"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
          {aFiles.length > 0 && (
            <div className={styles.containerButton}>
              <Button
                type="submit"
                variant="primary"
                onClick={(event) => publishFiles(event)}
                disabled={oCreateFormRes.loading || isValidForm}
                isLoading={oCreateFormRes.loading}
              >
                {intl.get("document.publish")}
              </Button>
            </div>
          )}
        </ContentBlank>
      </form>
    </div >
  )
}
export default ImportDocuments


